<template>
    <!-- Modal de Atajos de Teclado -->
    <base-modal
        v-model="showHelp"
        title="⌨️ Atajos de Teclado"
        icon="fas fa-keyboard"
        :show-footer="false"
        size="medium"
    >
        <div class="shortcuts-list">
            <!-- Guardar -->
            <div class="shortcut-item">
                <span class="shortcut-description">Guardar nota</span>
                <kbd class="shortcut-key">
                    <template v-if="isMac">Ctrl + ⌘ + S</template>
                    <template v-else>Ctrl + S</template>
                </kbd>
            </div>
            
            <!-- Nueva -->
            <div class="shortcut-item">
                <span class="shortcut-description">Nueva nota</span>
                <kbd class="shortcut-key">
                    <template v-if="isMac">Ctrl + ⌘ + N</template>
                    <template v-else>Ctrl + N</template>
                </kbd>
            </div>
            
            <!-- Lista (NUEVO) -->
            <div class="shortcut-item">
                <span class="shortcut-description">Ver lista de notas</span>
                <kbd class="shortcut-key">
                    <template v-if="isMac">Ctrl + ⌘ + L</template>
                    <template v-else>Ctrl + L</template>
                </kbd>
            </div>
            
            <!-- Cancelar -->
            <div class="shortcut-item">
                <span class="shortcut-description">Cancelar</span>
                <kbd class="shortcut-key">Escape</kbd>
            </div>
            
            <!-- Ayuda -->
            <div class="shortcut-item special">
                <span class="shortcut-description">Mostrar/ocultar ayuda</span>
                <kbd class="shortcut-key">
                    <template v-if="isMac">Ctrl + ⌘ + ?</template>
                    <template v-else>Ctrl + ?</template>
                </kbd>
            </div>
        </div>

        <p class="shortcuts-footer-text">Usa estos atajos para trabajar más rápido</p>
    </base-modal>
</template>

<script setup>
import { ref, onUnmounted } from 'vue';
import BaseModal from '@/components/BaseModal.vue';

defineProps({
    isMac: {
        type: Boolean,
        default: false
    }
});

const showHelp = ref(false);

const toggleHelp = () => {
    showHelp.value = !showHelp.value;
};

const closeHelp = () => {
    showHelp.value = false;
};

// Exponer método para uso externo
defineExpose({
    toggleHelp
});

// Cerrar con tecla Esc
const handleEscape = (event) => {
    if (event.key === 'Escape' && showHelp.value) {
        event.preventDefault(); // Prevenir comportamiento por defecto
        event.stopPropagation(); // Detener propagación para evitar que otros listeners lo procesen
        closeHelp();
    }
};

// Mostrar/ocultar con Ctrl/?
const handleHelp = (event) => {
    const isCtrlOrCmd = navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? event.metaKey : event.ctrlKey;
    if (isCtrlOrCmd && event.key === '?') {
        event.preventDefault();
        toggleHelp();
    }
};

// Registrar listeners en fase de captura (se ejecutan ANTES que los del composable)
document.addEventListener('keydown', handleEscape, true); // true = fase de captura
document.addEventListener('keydown', handleHelp);

// Limpiar listeners al desmontar
onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape, true);
    document.removeEventListener('keydown', handleHelp);
});
</script>

<style scoped>
/* Estilos específicos de la lista de atajos */
.shortcuts-footer-text {
    margin: 1rem 0 0 0;
    color: #6c757d;
    font-size: 0.875rem;
    text-align: center;
}

.shortcuts-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.shortcut-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #eee;
}

.shortcut-item:last-child {
    border-bottom: none;
}

.shortcut-item.special {
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 2px solid #667eea;
    border-bottom: none;
}

.shortcut-description {
    color: #4a5568;
    font-size: 0.95rem;
}

.shortcut-key {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    color: #667eea;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
